import { readableReferenceTitle } from "../lib/mediaAssetNaming";

/** Synthetic jobId prefix minted by `dispatchUgcStartImageLocal` for the
 *  Gemini-direct local-Banan start-image path. These jobs resolve
 *  synchronously inside the Django `/api/ad-platforms/local-banan/start-image/`
 *  round-trip and therefore have NO `/core/agent/managed-chat/status/:jobId`
 *  record. Any poller that hits that endpoint MUST skip them — otherwise it
 *  404s forever and freezes a "Generating…" card that never resolves.
 *
 *  Single source of truth — imported by `useAdJobsPoller`,
 *  `RunningAgentsPanel`, and the sub-agent sessionStorage persistence layer.
 *  Keep in sync with the mint format in `useAdDispatch.ts`. */
export const LOCAL_BANAN_JOB_PREFIX = "local-banan-";

export function isSyntheticLocalBananJob(jobId: string): boolean {
  return jobId.startsWith(LOCAL_BANAN_JOB_PREFIX);
}

export interface BananImageResult {
  id: string;
  status: "done" | "failed" | "rejected" | "pending" | "running" | string;
  output?: string;
  failed_reason?: string;
  credits_used?: number;
  created_at?: string;
}

export interface KlingVideoResult {
  id: string;
  status: "done" | "failed" | "rejected" | "pending" | "running" | string;
  video_output?: string;
  video_thumbnail?: string;
  duration?: number;
  failed_reason?: string;
  created_at?: string;
}

export type GeneratedAd =
  | {
      type: "image";
      result: BananImageResult;
      templateName?: string;
      templateId?: string;
      metadata?: Record<string, unknown>;
    }
  | { type: "video"; result: KlingVideoResult; metadata?: Record<string, unknown> };

export interface ReferenceImage {
  url: string;
  /** Present for product photos; absent for UGC element/scene refs. */
  productId?: string;
  /** Present for product photos; absent for UGC element/scene refs. */
  productTitle?: string;
  /** Element-based pipeline kinds + legacy ugc-scene-ref (read-only for old artifacts). */
  kind?:
    | "product-photo"
    | "ugc-element-product"
    | "ugc-element-item"
    | "ugc-element-bg"
    | "ugc-avatar-pose"
    | "ugc-text-frame"
    | "ugc-scene-ref";
  /** For element kinds: stable reuse key (e.g. "bg_bathroom_morning"). */
  elementKey?: string;
  /** For element kinds: registered Kling element_id (once available). */
  klingElementId?: number;
  /** For element kinds: number of planned videos that cite this elementKey
   *  (sourced from the product-scoped `ugc-video-plan` artifacts via
   *  `useElementPlansForProduct`). */
  reuseCount?: number;
  /** For ugc-text-frame: the assigned beat for this text frame. */
  assignedBeat?: "HOOK" | "SETUP" | "REVEAL" | "PAYOFF";
  /** Legacy: scene slot from old storyboard pipeline. */
  ugcSceneSlot?: "hook" | "setup" | "reveal" | "payoff";
  ugcSceneIndex?: number;
  sourceVideoScriptAngle?: string;
  /** Generated-asset id for linking back. */
  assetId?: string;
}

export interface GroupedGeneratedAd {
  ad: GeneratedAd;
  originalIndex: number;
  variantCount: number;
}

/** Build a display caption for a reference image. */
export function captionFor(image: ReferenceImage): string {
  switch (image.kind) {
    case "ugc-element-product":
    case "ugc-element-item":
    case "ugc-element-bg":
    case "ugc-avatar-pose":
      return readableReferenceTitle(image, { includeReuse: true });
    case "ugc-text-frame":
      return readableReferenceTitle(image);
    case "ugc-scene-ref": {
      return readableReferenceTitle(image);
    }
    default:
      return image.productTitle ?? "";
  }
}

/**
 * Deduplicates image ads by templateId, keeping only the first representative
 * per template group. Returns items with their original indices for detail modal navigation.
 */
export function groupAdsByTemplate(
  ads: GeneratedAd[],
): { ad: GeneratedAd; originalIndex: number }[] {
  const seenTemplates = new Set<string>();
  const items: { ad: GeneratedAd; originalIndex: number }[] = [];
  for (let i = 0; i < ads.length; i++) {
    const ad = ads[i];
    if (ad.type === "image" && ad.templateId) {
      if (seenTemplates.has(ad.templateId)) continue;
      seenTemplates.add(ad.templateId);
    }
    items.push({ ad, originalIndex: i });
  }
  return items;
}

/**
 * Get all image ads that share the same templateId (variation siblings).
 * Excludes the ad at the given index.
 */
export function getVariationSiblings(
  ads: GeneratedAd[],
  adIndex: number,
): { ad: GeneratedAd; index: number }[] {
  const ad = ads[adIndex];
  if (!ad || ad.type !== "image" || !ad.templateId) return [];
  return ads
    .map((a, i) => ({ ad: a, index: i }))
    .filter(
      ({ ad: a, index: i }) =>
        i !== adIndex &&
        a.type === "image" &&
        (a as Extract<GeneratedAd, { type: "image" }>).templateId === ad.templateId,
    );
}

function metadataForAd(ad: GeneratedAd): Record<string, unknown> {
  return ad.metadata ?? {};
}

function metadataString(meta: Record<string, unknown>, key: string): string | undefined {
  const value = meta[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function metadataNumber(meta: Record<string, unknown>, key: string): number | undefined {
  const value = meta[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function resultIdForAd(ad: GeneratedAd): string | undefined {
  const value = ad.result.id;
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function mediaUrlForAd(ad: GeneratedAd): string | undefined {
  return ad.type === "image" ? ad.result.output : ad.result.video_output;
}

function batchJobKey(ad: GeneratedAd, prefix: "image" | "video"): string | null {
  const meta = metadataForAd(ad);
  const jobId = metadataString(meta, "jobId");
  if (!jobId) return null;
  const id = resultIdForAd(ad);
  const batchIndex = metadataNumber(meta, "batchIndex");
  if (batchIndex != null || id?.startsWith(`${jobId}_`)) {
    return `${prefix}-job:${jobId}`;
  }
  return null;
}

function cloneSegmentAJobId(ad: GeneratedAd): string | null {
  if (ad.type !== "video") return null;
  const meta = metadataForAd(ad);
  if (meta.is30s === true) {
    return (
      metadataString(meta, "segmentAJobId") ??
      metadataString(meta, "jobId") ??
      resultIdForAd(ad)?.replace(/_stitched_30s$/, "") ??
      null
    );
  }
  if (meta.isStitchSegment === true) {
    const segmentIndex = metadataNumber(meta, "pairedSegmentIndex");
    if (segmentIndex === 0) {
      return metadataString(meta, "jobId") ?? resultIdForAd(ad)?.replace(/_segment_a$/, "") ?? null;
    }
    if (segmentIndex === 1) {
      return metadataString(meta, "pairedSegmentJobId") ?? null;
    }
  }

  const resultId = resultIdForAd(ad);
  if (resultId?.endsWith("_stitched_30s")) {
    return resultId.replace(/_stitched_30s$/, "");
  }
  if (resultId?.endsWith("_segment_a")) {
    return resultId.replace(/_segment_a$/, "");
  }
  if (metadataString(meta, "segmentAVideoUrl") && metadataString(meta, "segmentBVideoUrl")) {
    return metadataString(meta, "segmentAJobId") ?? metadataString(meta, "jobId") ?? null;
  }
  return null;
}

function projectGroupKeyForAd(ad: GeneratedAd): string | null {
  if (ad.type === "image") {
    const batchKey = batchJobKey(ad, "image");
    if (batchKey) return batchKey;
    if (ad.templateId) return `image-template:${ad.templateId}`;
    const meta = metadataForAd(ad);
    const templateId = metadataString(meta, "templateId");
    return templateId ? `image-template:${templateId}` : null;
  }

  const segmentAJobId = cloneSegmentAJobId(ad);
  if (segmentAJobId) return `video-clone:${segmentAJobId}`;
  return batchJobKey(ad, "video");
}

function representativeRank(ad: GeneratedAd): number {
  if (ad.type !== "video") return 0;
  const meta = metadataForAd(ad);
  if (meta.is30s === true || resultIdForAd(ad)?.endsWith("_stitched_30s")) {
    return 3;
  }
  if (meta.isStitchSegment === true) return 1;
  return 2;
}

function addAssetIdentity(ad: GeneratedAd, seen: Set<string>) {
  const url = mediaUrlForAd(ad);
  if (url) {
    seen.add(`url:${url}`);
    return;
  }
  const id = resultIdForAd(ad);
  if (id) seen.add(`id:${id}`);
}

function addEmbeddedVideoSegmentIdentities(ad: GeneratedAd, seen: Set<string>) {
  if (ad.type !== "video") return;
  const meta = metadataForAd(ad);
  const segmentAUrl = metadataString(meta, "segmentAVideoUrl");
  const segmentBUrl = metadataString(meta, "segmentBVideoUrl");
  if (segmentAUrl) seen.add(`url:${segmentAUrl}`);
  if (segmentBUrl) seen.add(`url:${segmentBUrl}`);
}

export function getProjectVariationSiblings(
  ads: GeneratedAd[],
  adIndex: number,
): { ad: GeneratedAd; index: number }[] {
  const ad = ads[adIndex];
  if (!ad) return [];
  const groupKey = projectGroupKeyForAd(ad);
  if (!groupKey) return getVariationSiblings(ads, adIndex);
  return ads
    .map((candidate, index) => ({ ad: candidate, index }))
    .filter(
      ({ ad: candidate, index }) =>
        index !== adIndex && projectGroupKeyForAd(candidate) === groupKey,
    );
}

export function getProjectVariantCount(ads: GeneratedAd[], adIndex: number): number {
  const ad = ads[adIndex];
  if (!ad) return 1;
  const seen = new Set<string>();
  addAssetIdentity(ad, seen);
  addEmbeddedVideoSegmentIdentities(ad, seen);
  for (const sibling of getProjectVariationSiblings(ads, adIndex)) {
    addAssetIdentity(sibling.ad, seen);
    addEmbeddedVideoSegmentIdentities(sibling.ad, seen);
  }
  return Math.max(1, seen.size);
}

export function groupGeneratedAdsForProjects(ads: GeneratedAd[]): GroupedGeneratedAd[] {
  const keyed = new Map<
    string,
    { items: { ad: GeneratedAd; originalIndex: number }[]; representativeIndex: number }
  >();
  const ungrouped: { ad: GeneratedAd; originalIndex: number }[] = [];

  ads.forEach((ad, originalIndex) => {
    const key = projectGroupKeyForAd(ad);
    if (!key) {
      ungrouped.push({ ad, originalIndex });
      return;
    }
    const group = keyed.get(key);
    if (!group) {
      keyed.set(key, {
        items: [{ ad, originalIndex }],
        representativeIndex: 0,
      });
      return;
    }
    group.items.push({ ad, originalIndex });
    const current = group.items[group.representativeIndex];
    if (representativeRank(ad) > representativeRank(current.ad)) {
      group.representativeIndex = group.items.length - 1;
    }
  });

  const grouped = Array.from(keyed.values()).map((group) => {
    const representative = group.items[group.representativeIndex];
    return {
      ...representative,
      variantCount: getProjectVariantCount(ads, representative.originalIndex),
    };
  });

  return [
    ...ungrouped.map((item) => ({
      ...item,
      variantCount: getProjectVariantCount(ads, item.originalIndex),
    })),
    ...grouped,
  ];
}
